import React, {useCallback, useEffect, useMemo, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {DialogContent, DialogTitle, Divider, SvgIcon} from "@mui/material";
import Dialog from "../../../base/dialog";
import {Col} from "reactstrap";
import Form from "../../../base/form";
import Button from "../../../base/button/button";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import SaveOrRollback from "../../../app-specific/save-or-rollback";
import IconButton from "../../../base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import {Edit} from "@mui/icons-material";
import {ReactComponent as Copy} from "../../../../../assets/images/copy.svg";
import CopyToClipboard from "../../../base/copy-to-clipboard";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    useDataGridSavedState
} from "../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../containers/data-grid";
import {ProductTechnicalFileSharedLinksDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import useAccessLevels from "../../../../hooks/use-access-levels";

const createSchema = Yup.object().shape({
    newTitle: Yup.string().trim().nullable().required(ValidateMessages.required)
});

const createValidate = makeValidate(createSchema);
const createRequired = makeRequired(createSchema);

const getLinkId = (link) => `link-${link.id}`;

const dataGridSaveConfig = {
    name: 'ManageProductTechnicalFilesSharedLinksDialogDataGrid',
    version: 1,
}

const ManageProductTechnicalFilesSharedLinksDialog = ({technicalFile, open, close, partNo}) => {
    const {packages} = useAccessLevels();
    const canShareTechnicalFileLink = packages.clientPlatform.shareTechnicalFileLink;
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [schema, setSchema] = useState(Yup.object().shape({}));
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();

    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    /**
     * With each change in open state of the dialog:
     * - if open then fetches the share links of the current technical file
     * - if closed, resets the link state.
     */
    useEffect(() => {
        if (!open || !packages.clientPlatform.shareTechnicalFileLink) {
            setLinks([]);
            return;
        }
        getShareLinks().then();
    }, [open, packages.clientPlatform.shareTechnicalFileLink])

    /**
     * Fetches the shared link of the current technical file belonging to the current product.
     * @return {Promise<void>}
     */
    const getShareLinks = async () => {
        if (!packages.clientPlatform.shareTechnicalFileLink) {
            return;
        }
        setLoading(true);
        let response = await Api.getOperatorPartTechnicalFileShareLinks(technicalFile.id, partNo);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            const links = response?.data?.map(e => ({
                ...e,
                inEditMode: false,
                editing: false,
                removing: false,
            })) ?? []
            setupSchemaAndInitialValues(links);
            setLinks(links);
        }
        setLoading(false);
    }

    /**
     * Create a new link for the current technical file.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<void>}
     */
    const createTechnicalFileLink = async (values, form) => {
        if (!packages.clientPlatform.shareTechnicalFileLink) {
            return;
        }
        setCreating(true)
        const forApi = {
            documentId: technicalFile.id,
            name: values.newTitle?.trim(),
            partNo: partNo,
        }
        const response = await Api.createOperatorPartTechnicalFileShareLink(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setLinks(prevState => [
                ...prevState,
                {
                    ...response.data,
                    inEditMode: false,
                    editing: false,
                    removing: false
                }
            ])
            form.change('newTitle', '')
            form.resetFieldState('newTitle');
        }
        setCreating(false)
    }

    /**
     * Updates the name of the given technical file link in the server
     * Also updates the name of a shared link in the state
     * @param link
     * @param newName
     * @return {Promise<void>}
     */
    const updateTechnicalFileLink = async (link, newName) => {
        if (!canShareTechnicalFileLink) return;
        const forApi = {
            token: link?.link.split('/').pop(),
            title: newName?.trim() ?? '',
        }
        setLinks(prevState => prevState?.map(e => e.id === link.id
            ? {...e, editing: true}
            : e
        ))
        const response = await Api.updateTechnicalFileShareLinkTitle(forApi);
        if (!isMounted()) return;
        setLinks(prevState => prevState?.map(e => e.id === link.id
            ? {...e, editing: false}
            : e
        ))
        if (response?.resultFlag) {
            setLinks(prevState => prevState?.map(e => e.id === link.id
                ? {...e, name: newName, inEditMode: false}
                : e
            ))
        }
    }

    /**
     * Removes the given technical file link in the server.
     * @param link
     * @return {Promise<void>}
     */
    const removeTechnicalFileLink = async (link) => {
        if (!canShareTechnicalFileLink) return;
        setLinks(prevState => prevState?.map(e => e.id === link.id ? {...e, removing: true} : e));
        const response = await Api.removeOperatorPartTechnicalFileShareLink(link.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setLinks(prevState => prevState?.filter(e => e.id !== link.id))
        } else {
            setLinks(prevState => prevState?.map(e => e.id === link.id
                ? {...e, removing: false}
                : e
            ))
        }
    }

    /**
     * Sets up the schema and initial values used in the form of the table.
     * @param links
     */
    const setupSchemaAndInitialValues = (links) => {
        const schema = {};
        const initialValues = {};
        for (const link of links) {
            const linkId = getLinkId(link);
            schema[linkId] = Yup.string().nullable().trim().required(ValidateMessages.required);
            initialValues[linkId] = link.name;
        }
        setSchema(Yup.object().shape(schema));
        setInitialValues(initialValues);
    }

    /**
     * Toggles the edit mode of the provided link.
     * @param link
     */
    const toggleLinkEditMode = (link) => {
        setLinks(prevState => prevState.map(e => e.id === link.id
            ? {...e, inEditMode: !e.inEditMode}
            : e
        ))
    }

    /**
     * Closes the edit mode of the given link and reverts any changes made to its values in the form.
     * @param link
     * @param form
     */
    const revertLinkValues = (link, form) => {
        const name = getLinkId(link);
        form.change(name, link.name);
        toggleLinkEditMode(link);
    }

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                title: 'Name',
                name: ProductTechnicalFileSharedLinksDataGridColumnNames.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 150,
                sortable: false,
            },
            {
                title: 'Views Count',
                name: ProductTechnicalFileSharedLinksDataGridColumnNames.views,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Link',
                name: ProductTechnicalFileSharedLinksDataGridColumnNames.link,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                    minWidth: 250,
                }
            },
            {
                name: ProductTechnicalFileSharedLinksDataGridColumnNames.copy,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 0,
            },
            {
                name: ProductTechnicalFileSharedLinksDataGridColumnNames.remove,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 0,
            },
        ], [])

    const [dataGridColumns, _, __, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    /**
     * @type {function(*, *, *): DataGridRow[]}
     */
    const dataGridRows = useCallback((initialValues, values, form) => {
        return links?.map(link => {
            const name = getLinkId(link);
            return ({
                key: link.id,
                data: link,
                cells: {
                    [ProductTechnicalFileSharedLinksDataGridColumnNames.name]: link.inEditMode
                        ? <div className={'d-flex align-items-center'}>
                            <OutlinedInputForm
                                name={name}
                                placeholder={'name...'}
                                label={"Name"}
                                className={'mr-2'}
                                required={required[name]}
                                value={values[name] ?? ''}
                                onChange={(e) => form.change(e.target.name, e.target.value)}
                            />
                            <SaveOrRollback
                                show={typeof values[name] !== "undefined" && values[name] !== null}
                                column={false}
                                showText={false}
                                loading={link.editing}
                                onRollback={() => revertLinkValues(link, form)}
                                onSave={() => values[name] === initialValues[name]
                                    ? revertLinkValues(link, form)
                                    : updateTechnicalFileLink(link, values[name])
                                }
                            />
                        </div>
                        : <div className={'d-flex align-items-center'}>
                            <p {...dataGridCellTextProps} className={'text-sm black mr-2'}>
                                {link?.name ?? ''}
                            </p>
                            <IconButton
                                onPressed={() => toggleLinkEditMode(link)}
                                disabled={link?.inEditMode}
                                color={'paper.secondary'}
                            >
                                <Edit className={"icon small"}/>
                            </IconButton>
                        </div>,
                    [ProductTechnicalFileSharedLinksDataGridColumnNames.views]: link.viewCount ?? 0,
                    [ProductTechnicalFileSharedLinksDataGridColumnNames.link]: link.link?.replace('$PARTNUMBER', partNo) ?? '--',
                    [ProductTechnicalFileSharedLinksDataGridColumnNames.copy]:
                        <CopyToClipboard>
                            {({copy}) =>
                                <IconButton
                                    onPressed={() => copy(link.link?.replace('$PARTNUMBER', partNo) ?? '--')}
                                    color={'paper.secondary'}
                                    className={'copy-to-clipboard-button'}
                                >
                                    <SvgIcon className={'icon small'} component={Copy}/>
                                </IconButton>
                            }
                        </CopyToClipboard>,
                    [ProductTechnicalFileSharedLinksDataGridColumnNames.remove]:
                        <IconButton
                            onPressed={() => removeTechnicalFileLink(link)}
                            disabled={link.removing || link.editing}
                            color={'paper.error'}
                        >
                            <SvgIcon component={RemoveIcon} className='icon normal'/>
                        </IconButton>
                },
            })
        });
    }, [links, partNo, required])

    if (!canShareTechnicalFileLink) {
        return null;
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'md'}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Manage Documents' shared links
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-3 py-2 d-flex flex-wrap'}>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-center'
                        onSubmit={createTechnicalFileLink}
                        validate={createValidate}
                        initialValues={{}}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col xs={12}>
                                        <p className={'text-sm black mb-3 font-weight-semi-bold'}>
                                            Create a new Shared link:
                                        </p>
                                    </Col>
                                    <Col className={'pl-3'}>
                                        <OutlinedInputForm
                                            name={'newTitle'}
                                            placeholder={'title'}
                                            label={"New Title"}
                                            required={createRequired.newTitle}
                                            value={values.newTitle ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={'auto'}>
                                        <Button
                                            className=' py-2-5 px-5'
                                            variant={'contained'}
                                            color={'primary'}
                                            type={'submit'}
                                            disabled={creating}
                                        >
                                            {
                                                creating
                                                    ? "Creating..."
                                                    : "Create"
                                            }
                                        </Button>
                                    </Col>
                                </>
                            )
                        }}
                    />
                    <Form
                        className='d-flex flex-wrap w-100 align-items-center'
                        onSubmit={() => false}
                        validate={validate}
                        initialValues={initialValues}
                        render={({initialValues, values, form}) => {
                            const rows = dataGridRows(initialValues, values, form)
                            return (
                                <>
                                    <PartnerDataGrid
                                        {...dataGridSaveConfig}
                                        initialLayoutState={{
                                            columns: columns,
                                        }}
                                        rows={rows}
                                        columns={dataGridColumns}
                                        hideExporting
                                        hideFooterActions
                                        hideFooter
                                        visibleRows={5}
                                        state={{
                                            loading: {state: loading},
                                            density: dataGridDensity,
                                        }}
                                    />
                                    <button type={'submit'} className={'invisible sr-only'}/>
                                </>
                            );
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}


export default ManageProductTechnicalFilesSharedLinksDialog;
